/**
 * Thrown for a 422 validation failure. The message is the backend's own
 * violation text, safe to show the user as-is. Other failures (401/500/
 * network) throw a plain Error with a generic fallback instead.
 */
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Symfony's validation-failure response (RFC 9457 problem+json) carries the
 * actual field error in `violations[].title`. Falls back to `fallback` when
 * the body isn't in that shape.
 */
async function extractErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const body = await response.json();
    const message = body?.violations?.[0]?.title ?? body?.detail;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  } catch {
    // Response body wasn't JSON — fall through to the fallback message.
  }
  return fallback;
}

/**
 * Throws a ValidationError carrying the backend's message for a 422
 * response, or a plain Error with `fallback` for any other failure.
 */
export async function throwApiError(
  response: Response,
  fallback: string
): Promise<never> {
  if (response.status === 422) {
    throw new ValidationError(await extractErrorMessage(response, fallback));
  }
  throw new Error(fallback);
}
